import { Injectable, inject, signal } from '@angular/core';
import { FeatureEntity } from '@gishub/domain';
import { ArcGisQueryRepositoryProvider } from '../../shared/providers/repository-providers';
import { LocalInterventionService, filterLocalByNames } from './local-intervention-service';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import Polygon from '@arcgis/core/geometry/Polygon';
import Polyline from '@arcgis/core/geometry/Polyline';
import Point from '@arcgis/core/geometry/Point';

export interface LayerTarget {
  key: string;
  label: string;
  badge: string;
  url: string;
  hasProxy: boolean;
}

export interface IntersectionCount {
  key: string;
  label: string;
  badge: string;
  count: number;
}

@Injectable({ providedIn: 'root' })
export class CommunityIntersectionService {
  private queryRepo = inject(ArcGisQueryRepositoryProvider).instance;
  private readonly _localInterventions = inject(LocalInterventionService);

  readonly selectedFeature = signal<FeatureEntity | null>(null);
  readonly loading = signal(false);
  readonly counts = signal<IntersectionCount[]>([]);
  readonly communityName = signal('');
  readonly intersectingIds = signal<Record<string, number[]>>({});
  readonly idFields = signal<Record<string, string>>({});

  private _communityGeom: Polygon | Polyline | Point | null = null;

  async analyze(
    feature: FeatureEntity,
    targets: LayerTarget[],
    where: string,
  ): Promise<void> {
    this.selectedFeature.set(feature);
    this.communityName.set(this._extractName(feature));
    this._communityGeom = this._ensureGeographic(this._toArcGISGeom(feature.geometry));

    if (!this._communityGeom) {
      this.loading.set(false);
      this.counts.set([]);
      return;
    }

    this.loading.set(true);
    this.counts.set([]);
    this.intersectingIds.set({});
    this.idFields.set({});

    const results: IntersectionCount[] = [];
    const idsMap: Record<string, number[]> = {};
    const idFieldMap: Record<string, string> = {};

    const queries = targets.map(t =>
      this.queryRepo
        .queryLayerFeatures(
          t.url,
          where,
          undefined,
          'esriGeometryPolygon',
          'esriSpatialRelIntersects',
          '*',
          true,
          0,
          2000,
          t.hasProxy,
        )
        .then(res => {
          let data = res.success && res.data ? res.data : [];
          if (t.key === 'intervenciones') {
            // Prepender inversiones locales (coordenadas aproximadas) del ámbito de la comunidad
            const f = this.selectedFeature();
            const a = ((f?.attributes ?? {}) as Record<string, any>);
            data = [
              ...filterLocalByNames(this._localInterventions.getLocalFeatures(), {
                region: String(a['nombdep'] || a['DEPARTAMENTO'] || a['nom_dpto'] || '').trim() || null,
                provincia: String(a['nombprov'] || a['PROVINCIA'] || a['nom_prov'] || '').trim() || null,
                distrito: String(a['nombdist'] || a['distrito'] || a['nom_dist'] || '').trim() || null,
              }),
              ...data,
            ];
          }
          const intersecting = data.filter(f =>
            f.attributes['_local'] === true || this._intersects(f.geometry),
          );
          results.push({
            key: t.key,
            label: t.label,
            badge: t.badge,
            count: intersecting.length,
          });
          if (intersecting.length > 0) {
            const idField = this._detectIdField(intersecting[0].attributes as Record<string, any>);
            idFieldMap[t.key] = idField;
            idsMap[t.key] = intersecting.map(f => Number((f.attributes as Record<string, any>)[idField])).filter(id => !isNaN(id));
          }
        })
        .catch(() => {
          results.push({ key: t.key, label: t.label, badge: t.badge, count: 0 });
        }),
    );

    await Promise.all(queries);
    this.counts.set(results);
    this.intersectingIds.set(idsMap);
    this.idFields.set(idFieldMap);
    this.loading.set(false);
  }

  getFilteredWhere(key: string, baseWhere: string): string {
    const ids = this.intersectingIds()[key];
    const idField = this.idFields()[key];
    if (ids && ids.length > 0 && idField) {
      return `(${baseWhere}) AND ${idField} IN (${ids.join(',')})`;
    }
    return baseWhere;
  }

  clear(): void {
    this.selectedFeature.set(null);
    this.loading.set(false);
    this.counts.set([]);
    this.communityName.set('');
    this.intersectingIds.set({});
    this.idFields.set({});
    this._communityGeom = null;
  }

  private _detectIdField(attrs: Record<string, any>): string {
    const candidates = ['objectid', 'OBJECTID', 'ObjectId', 'fid', 'FID', 'id', 'Id', 'ID'];
    for (const c of candidates) {
      if (c in attrs) return c;
    }
    for (const [key, val] of Object.entries(attrs)) {
      if (typeof val === 'number') return key;
    }
    return 'objectid';
  }

  private _intersects(featureGeom: any): boolean {
    if (!this._communityGeom || !featureGeom) return false;
    try {
      const arcGeom = this._ensureGeographic(this._toArcGISGeom(featureGeom));
      if (!arcGeom) return false;
      if (!this._sameSR(this._communityGeom, arcGeom)) return false;
      return geometryEngine.intersects(this._communityGeom, arcGeom);
    } catch {
      return false;
    }
  }

  private _sameSR(a: any, b: any): boolean {
    const sa = a.spatialReference;
    const sb = b.spatialReference;
    if (!sa || !sb) return false;
    return sa.wkid === sb.wkid;
  }

  private _ensureGeographic(geom: any): any {
    if (!geom) return null;
    const sr = geom.spatialReference;
    const isWebMerc = sr && (sr.wkid === 3857 || sr.wkid === 102100 || sr.wkid === 102113);
    if (isWebMerc) {
      return webMercatorUtils.webMercatorToGeographic(geom);
    }
    const val = this._firstCoord(geom);
    if (!sr && val != null && Math.abs(val) > 180) {
      const projected = webMercatorUtils.webMercatorToGeographic(geom);
      if (projected) return projected;
    }
    return geom;
  }

  private _firstCoord(geom: any): number | undefined {
    if (geom.x != null) return geom.x;
    if (geom.rings?.[0]?.[0]?.[0] != null) return geom.rings[0][0][0];
    if (geom.paths?.[0]?.[0]?.[0] != null) return geom.paths[0][0][0];
    if (geom.coordinates?.[0]?.[0]?.[0] != null) return geom.coordinates[0][0][0];
    if (geom.coordinates?.[0]?.[0] != null) return geom.coordinates[0][0];
    return undefined;
  }

  private _toArcGISGeom(geom: any): Polygon | Polyline | Point | null {
    if (!geom) return null;
    const isGeoJson =
      geom.type != null &&
      geom.coordinates != null &&
      geom.x == null &&
      geom.paths == null &&
      geom.rings == null;

    try {
      if (isGeoJson) {
        const sr = geom.spatialReference ?? { wkid: 4326 };
        if (geom.type === 'Point' || geom.type === 'MultiPoint') {
          const coords = geom.type === 'MultiPoint' ? geom.coordinates[0] : geom.coordinates;
          return new Point({ x: coords[0], y: coords[1], spatialReference: sr });
        }
        if (geom.type === 'Polygon') {
          const rings = geom.coordinates.map((ring: number[][]) =>
            ring.map((c: number[]) => [c[0], c[1]]),
          );
          return Polygon.fromJSON({ rings, spatialReference: sr });
        }
        if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
          const paths = geom.type === 'MultiLineString'
            ? geom.coordinates
            : [geom.coordinates];
          return Polyline.fromJSON({ paths, spatialReference: sr });
        }
        return null;
      }

      const sr = geom.spatialReference ?? this._detectSR(geom);
      if (geom.rings) {
        return Polygon.fromJSON({ rings: geom.rings, spatialReference: sr });
      }
      if (geom.paths) {
        return Polyline.fromJSON({ paths: geom.paths, spatialReference: sr });
      }
      if (geom.x != null) {
        return new Point({ x: geom.x, y: geom.y, spatialReference: sr });
      }
      return null;
    } catch {
      return null;
    }
  }

  private _detectSR(geom: any): { wkid: number } {
    const val = this._firstCoord(geom);
    if (val != null && Math.abs(val) > 180) {
      return { wkid: 3857 };
    }
    return { wkid: 4326 };
  }

  private _extractName(feature: FeatureEntity): string {
    const attrs = feature.attributes as Record<string, any>;
    const candidates = ['comunidad', 'nombre', 'name', 'nomb_cc', 'centro_poblado', 'nomb_cp'];
    for (const key of candidates) {
      const val = attrs[key];
      if (val != null && typeof val === 'string' && val.length > 2) return val;
    }
    const firstVal = Object.values(attrs).find(v => v != null && typeof v === 'string' && v.length > 3);
    return firstVal ?? 'Comunidad';
  }
}
